"use strict";
/**
 * Image URL Helper
 *
 * Provides consistent URL generation for images stored in S3/MinIO.
 * Handles relative paths, absolute URLs, and fallback to placeholders.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.S3_PUBLIC_URLS = exports.STATIC_ASSETS_URLS = void 0;
exports.resolveStaticAssetsUrl = resolveStaticAssetsUrl;
exports.getImageUrl = getImageUrl;
exports.extractRelativePath = extractRelativePath;
exports.getLogoUrl = getLogoUrl;
/**
 * Per-environment host for static frontend build assets (SFU-2 / D3, D5, D7).
 *
 * This host is the CDN-swap abstraction: a future CDN becomes a transparent
 * reverse-cache over the same origin via a DNS repoint — same URLs, same
 * objects, no rebuild. Both hosts CNAME to the `teletienda-static-assets`
 * bucket; dev and prod differ only by this base URL.
 */
exports.STATIC_ASSETS_URLS = {
    production: 'https://static.teletienda.ar',
    development: 'https://static-dev.teletienda.ar',
};
/** Platform-wide standardized S3 public URLs (Story 3.11 convention) */
exports.S3_PUBLIC_URLS = {
    products: 'https://s3.teletienda.ar/teletienda-products',
    logos: 'https://s3.teletienda.ar/merchant-logos',
    avatars: 'https://s3.teletienda.ar/teletienda-avatars',
    // Prod default, consistent with the other entries; use resolveStaticAssetsUrl()
    // for environment-aware resolution (SFU-2).
    staticAssets: exports.STATIC_ASSETS_URLS.production,
};
/**
 * Resolve the static-assets base host (no trailing slash) for an environment.
 *
 * Resolution order (D7 — single env-configured value, no raw S3 endpoint):
 *   1. explicit `override` (e.g. `VITE_ASSET_BASE_URL` / a CDN host) wins;
 *   2. else `env` maps to the dev/prod host;
 *   3. else defaults to production.
 *
 * Pure (no process.env / globals read) so callers stay deterministic and it is
 * trivially unit-testable; pass the environment in explicitly.
 *
 * @example
 * resolveStaticAssetsUrl({ env: 'development' }) // 'https://static-dev.teletienda.ar'
 * resolveStaticAssetsUrl({ override: 'https://cdn.example.com/' }) // 'https://cdn.example.com'
 */
function resolveStaticAssetsUrl(opts = {}) {
    const { env, override } = opts;
    if (override && override.trim()) {
        return override.trim().replace(/\/+$/, '');
    }
    return env === 'development' || env === 'dev'
        ? exports.STATIC_ASSETS_URLS.development
        : exports.STATIC_ASSETS_URLS.production;
}
/**
 * Generates full image URL from relative path
 *
 * @param relativePath - Relative path in S3 or absolute URL
 * @param config - Configuration with S3 base URL and placeholder
 * @returns Full URL to image or placeholder if path is empty
 *
 * @example
 * // Relative path
 * getImageUrl('merchants/abc/logo.webp', config)
 * // Returns: 'https://s3.teletienda.ar/teletienda-products/merchants/abc/logo.webp'
 *
 * @example
 * // Absolute URL (legacy)
 * getImageUrl('https://cdn.dummyjson.com/image.jpg', config)
 * // Returns: 'https://cdn.dummyjson.com/image.jpg'
 *
 * @example
 * // Empty path
 * getImageUrl(null, config)
 * // Returns: '/images/placeholder-product.png'
 */
function getImageUrl(relativePath, config) {
    if (!relativePath) {
        return config.placeholderUrl;
    }
    // Already absolute URL (legacy dummyjson or external)
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
        return relativePath;
    }
    // Relative path - prepend S3 base URL
    return `${config.s3PublicUrl}/${relativePath}`;
}
/**
 * Extracts relative path from full S3 URL
 *
 * @param fullUrl - Full URL to image
 * @param s3PublicUrl - S3 base URL to strip
 * @returns Relative path or original URL if not from our S3
 *
 * @example
 * extractRelativePath('https://s3.teletienda.ar/teletienda-products/merchants/abc/logo.webp', s3Url)
 * // Returns: 'merchants/abc/logo.webp'
 */
function extractRelativePath(fullUrl, s3PublicUrl) {
    if (fullUrl.startsWith(s3PublicUrl)) {
        return fullUrl.substring(s3PublicUrl.length + 1);
    }
    return fullUrl;
}
/**
 * Get merchant logo URL (deterministic, always exists)
 *
 * Merchant logos are stored in a dedicated bucket with predictable URLs.
 * Logo is either custom-uploaded or auto-generated (GitHub-style initials).
 *
 * @param merchantGuid - Merchant UUID
 * @param config - Configuration with S3 logo bucket URL
 * @returns Full URL to merchant logo WebP file
 *
 * @example
 * getLogoUrl('0169c682-bdce-4b69-8876-10aaf0d76d08', { s3LogoUrl: 'https://s3.teletienda.ar/merchant-logos' })
 * // Returns: 'https://s3.teletienda.ar/merchant-logos/0169c682-bdce-4b69-8876-10aaf0d76d08/logo.webp'
 */
function getLogoUrl(merchantGuid, config) {
    return `${config.s3LogoUrl}/${merchantGuid}/logo.webp`;
}
